import React from "react";
import {
    LineItemTemplate,
    LineItemType,
    PurchaseRule,
    PurchaseRuleShortcutTemplateCode,
    PurchaseRuleShortcutType,
} from "types";
import { useResource } from "../../../hooks/useResource";
import { Currency } from "../../Common/Currency";

interface TermSummaryProps {
    purchaseRule: PurchaseRule;
}

export const TermSummary = ({ purchaseRule }: TermSummaryProps) => {
    const shortcutData = purchaseRule.actions[0].shortcut;
    const templateCode = shortcutData?.options?.templateCodes?.[0];
    // if exists, template Id can either for Demographics or Addons templateIds.
    const templateIds = purchaseRule?.actions[0].shortcut?.options?.templateIds;
    const { data: lineItemTemplateForType } = useResource<LineItemTemplate>(["lineItemTemplates", templateIds?.[0]]);
    const lineItemType = lineItemTemplateForType?.type;

    if (shortcutData?.type === PurchaseRuleShortcutType.PERCENT) {
        if (
            templateCode === PurchaseRuleShortcutTemplateCode.PRE_TAX_SUBTOTAL ||
            templateCode === PurchaseRuleShortcutTemplateCode.PRE_TAX_FEE_SUBTOTAL
        ) {
            return (
                <span>
                    <var data-var="amount">{shortcutData.amount}</var>% of entire booking amount
                </span>
            );
        }

        if (
            templateCode === PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC ||
            lineItemType === LineItemType.DEMOGRAPHIC
        ) {
            return (
                <span>
                    <var data-var="amount">{shortcutData.amount}</var>% of demographic(s) amount
                </span>
            );
        }

        if (templateCode === PurchaseRuleShortcutTemplateCode.EQUIPMENT || lineItemType === LineItemType.EQUIPMENT) {
            return (
                <span>
                    <var data-var="amount">{shortcutData.amount}</var>% of purchasable equipment total
                </span>
            );
        }

        if (templateCode === PurchaseRuleShortcutTemplateCode.ADDON || lineItemType === LineItemType.ADDON) {
            return (
                <span>
                    <var data-var="amount">{shortcutData.amount}</var>% of add-on(s) amount
                </span>
            );
        }

        if (templateCode === PurchaseRuleShortcutTemplateCode.PICKUP_SUBTOTAL) {
            return (
                <span>
                    <var data-var="amount">{shortcutData.amount}</var>% pick-up location(s) amount
                </span>
            );
        }

        if (templateCode === PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL) {
            return (
                <span>
                    <var data-var="amount">{shortcutData.amount}</var>% Merchandise amount
                </span>
            );
        }
    }

    if (shortcutData?.type === PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE) {
        return (
            <span>
                <Currency>{shortcutData.amount}</Currency> per reservation
            </span>
        );
    }

    if (shortcutData?.type === PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT) {
        if (
            templateCode === PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC ||
            lineItemType === LineItemType.DEMOGRAPHIC
        ) {
            return (
                <span>
                    <Currency>{shortcutData.amount}</Currency> per purchasable demographic / per trip
                </span>
            );
        }

        if (templateCode === PurchaseRuleShortcutTemplateCode.EQUIPMENT || lineItemType === LineItemType.EQUIPMENT) {
            return (
                <span>
                    <Currency>{shortcutData.amount}</Currency> per purchasable equipment
                </span>
            );
        }

        if (templateCode === PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL) {
            return (
                <span>
                    <Currency>{shortcutData.amount}</Currency> per merchandise
                </span>
            );
        }

        return (
            <span>
                <Currency>{shortcutData.amount}</Currency> per add-on
            </span>
        );
    }

    if (templateCode === PurchaseRuleShortcutTemplateCode.PICKUP_SUBTOTAL) {
        return (
            <span>
                <var data-var="amount">{shortcutData?.amount}</var> pick-up location(s) amount
            </span>
        );
    }

    if (shortcutData?.type === PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT_PER_DAY) {
        if (templateCode === PurchaseRuleShortcutTemplateCode.EQUIPMENT || lineItemType === LineItemType.EQUIPMENT) {
            return (
                <span>
                    <Currency>{shortcutData.amount}</Currency> per purchasable equipment per day
                </span>
            );
        }

        return (
            <span>
                <Currency>{shortcutData.amount}</Currency> per purchasable demographic per day
            </span>
        );
    }

    return null;
};
